"use client";

import { memo, useState } from "react";
import styles from "./ProgramFilters.module.scss";
import FilterSlidersIcon from "@/SvgIcons/FilterSlidersIcon";
import ChevronDownIcon from "@/SvgIcons/ChevronDownIcon";
import CheckboxCheckedIcon from "@/SvgIcons/CheckboxCheckedIcon";
import PlusIcon from "@/SvgIcons/PlusIcon";
import clsx from "clsx";

export interface FilterSection {
  key: string;
  label: string;
  options: string[];
}

export interface ActiveFilters {
  [key: string]: string[];
}

export interface SubOptionConfig {
  options: string[];
  selected: string[];
  onToggle: (option: string) => void;
}

interface ProgramFiltersProps {
  sections: FilterSection[];
  activeFilters: ActiveFilters;
  onChange: (filters: ActiveFilters) => void;
  /** Pass true when rendering inside the mobile drawer so the aside is visible */
  drawerVisible?: boolean;
  /** Sub-options rendered under a specific filter option as a + dropdown.
   *  Shape: { [sectionKey]: { [optionLabel]: SubOptionConfig } } */
  subOptions?: Record<string, Record<string, SubOptionConfig>>;
  /** Options that are always checked and non-interactive.
   *  Shape: { [sectionKey]: string[] } */
  lockedOptions?: Record<string, string[]>;
}

const ProgramFilters = ({ sections, activeFilters, onChange, drawerVisible, subOptions, lockedOptions }: ProgramFiltersProps) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [expandedSubMenus, setExpandedSubMenus] = useState<Record<string, boolean>>({});

  const toggleSection = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleOption = (section: FilterSection, option: string) => {
    const current = activeFilters[section.key] ?? [];
    const next = current.includes(option)
      ? current.filter((o) => o !== option)
      : [...current, option];
    onChange({ ...activeFilters, [section.key]: next });
  };

  return (
    <aside
      {...(!drawerVisible ? { "data-program-filters": "" } : {})}
      className={`${styles.aside}${drawerVisible ? ` ${styles.drawerVisible}` : ""}`}
    >
      <div className={styles.asideScroll}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.heading}>Filters</span>
        <FilterSlidersIcon size={22} color="#1A325D" />
      </div>

      <div className={styles.topDivider} />

      {/* Filter sections */}
      {sections.map((section, idx) => {
        const isCollapsed = collapsed[section.key];
        const selected = activeFilters[section.key] ?? [];

        return (
          <div key={section.key}>
            <div className={styles.section}>
              {/* Section title row */}
              <button
                className={styles.sectionHeader}
                onClick={() => toggleSection(section.key)}
                aria-expanded={!isCollapsed}
              >
                <span className={styles.sectionTitle}>{section.label}</span>
                <ChevronDownIcon
                  size={16}
                  color="#243B6B"
                  className={isCollapsed ? styles.chevronCollapsed : styles.chevronExpanded}
                />
              </button>

              {/* Options list */}
              {!isCollapsed && (
                <div className={styles.options}>
                  {section.options.map((option) => {
                    const isLocked = (lockedOptions?.[section.key] ?? []).includes(option);
                    const checked = isLocked || selected.includes(option);
                    const subConfig = subOptions?.[section.key]?.[option];
                    const subKey = `${section.key}:${option}`;
                    const isSubOpen = !!expandedSubMenus[subKey];

                    return (
                      <div key={option} className={styles.optionGroup}>
                        {/* Main option row */}
                        <div className={styles.optionRow}>
                          <label className={`${styles.optionLabel}${isLocked ? ` ${styles.optionLabelLocked}` : ""}`}>
                            <span className={styles.checkbox} aria-hidden="true">
                              {checked ? (
                                <CheckboxCheckedIcon size={20} color="#fff" bg="#243B6B" />
                              ) : (
                                <span className={styles.checkboxEmpty} />
                              )}
                            </span>
                            <input
                              type="checkbox"
                              className={styles.hiddenInput}
                              checked={checked}
                              disabled={isLocked}
                              onChange={() => !isLocked && toggleOption(section, option)}
                            />
                            <span className={clsx(styles.optionText, "lining-nums")}>{option}</span>
                          </label>

                          {/* + toggle for options that have sub-options */}
                          {subConfig && (
                            <button
                              className={styles.subToggleBtn}
                              onClick={() =>
                                setExpandedSubMenus((prev) => ({ ...prev, [subKey]: !prev[subKey] }))
                              }
                              aria-expanded={isSubOpen}
                              aria-label={`${isSubOpen ? "Collapse" : "Expand"} ${option} sub-filters`}
                            >
                              <span className={`${styles.subPlusIcon} ${isSubOpen ? styles.subPlusIconOpen : ""}`}>
                                <PlusIcon size={12} color="#243B6B" />
                              </span>
                            </button>
                          )}
                        </div>

                        {/* Inline sub-options — expands below like an accordion */}
                        {subConfig && isSubOpen && (
                          <div className={styles.subOptions}>
                            {subConfig.options.map((sub) => {
                              const subChecked = subConfig.selected.includes(sub);
                              return (
                                <label key={sub} className={styles.subOptionLabel}>
                                  <span className={styles.checkbox} aria-hidden="true">
                                    {subChecked ? (
                                      <CheckboxCheckedIcon size={18} color="#fff" bg="#243B6B" />
                                    ) : (
                                      <span className={styles.checkboxEmpty} />
                                    )}
                                  </span>
                                  <input
                                    type="checkbox"
                                    className={styles.hiddenInput}
                                    checked={subChecked}
                                    onChange={() => subConfig.onToggle(sub)}
                                  />
                                  <span className={clsx(styles.optionText, "lining-nums")}>{sub}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Divider between sections (not after last) */}
            {idx < sections.length - 1 && (
              <div className={styles.sectionDivider} />
            )}
          </div>
        );
      })}
      </div>
    </aside>
  );
};

export default memo(ProgramFilters);
